<script lang="ts">
  import { focus } from "./actions/focus";
  import DialogBase from "./DialogBase.svelte";
  import { createImagekitConfig } from "./fs";
  import { makeFileSystemImagekit, testImagekitConnection } from "./fs-imagekit";
  import { captureEvent, telegramNotify } from "./http";
  import { showMessage } from "./message";

  interface Props {
    open?: boolean;
    mountfs: (fs: any) => void;
  }

  let { open = $bindable(false), mountfs }: Props = $props();

  let config = $state(createImagekitConfig());
  let testingConnection = $state(false);
  let errorMessage = $state("");

  let canConnect = $derived(config.privateKey.trim() != "" && !testingConnection);

  async function onConnect() {
    testingConnection = true;
    errorMessage = "";
    let removeMsg = showMessage("testing imagekit connection");
    try {
      config.privateKey = config.privateKey.trim();
      config.name = config.name.trim();
      await testImagekitConnection(config.privateKey);
      if (config.name == "") {
        config.name = "ImageKit";
      }
      let fs = makeFileSystemImagekit(config);
      let who = config.name;
      telegramNotify(`New ImageKit connection for ${who}`);
      captureEvent("imagekit-login", {});
      removeMsg();
      mountfs(fs);
      open = false;
    } catch (e) {
      console.error(e);
      errorMessage = (e as Error).toString();
      removeMsg();
    }
    testingConnection = false;
  }

  function close() {
    open = false;
  }
</script>

<DialogBase bind:open title="Connect ImageKit">
  {#snippet main()}
    <div class="flex flex-col text-sm">
      <label for="input-imagekit-name">Name: <span class="text-gray-400">(optional)</span></label>
      <input
        use:focus
        type="text"
        id="input-imagekit-name"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.name}
      />

      <label class="mt-3" for="input-imagekit-key">Private key:</label>
      <input
        type="password"
        id="input-imagekit-key"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.privateKey}
      />

      <div class="mt-3 w-72 text-gray-500">
        Get the private key from the
        <a
          href="https://imagekit.io/dashboard/developer/api-keys"
          class="text-blue-600 underline"
          target="_blank"
          rel="noreferrer">ImageKit dashboard</a
        >. It gives full access to your media library, so make one you can revoke.
      </div>

      {#if errorMessage}
        <div class="mt-2 mb-2 w-72 font-medium break-all text-red-500">
          {errorMessage}
        </div>
      {/if}
    </div>
  {/snippet}

  {#snippet bottom()}
    <div class="flex flex-row justify-between">
      <button onclick={close} class="dlg-btn">Cancel</button>
      <button disabled={!canConnect} onclick={onConnect} class="dlg-btn">Connect ImageKit</button>
    </div>
  {/snippet}
</DialogBase>
